/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { AutoApplyBankPromotion } from '@/components/blocks/AutoApplyBankPromotion';
import { DpCookieDialogRecommendation } from '@/components/blocks/DpCookieDialogRecommendation';
import { GlobalStylesContent } from '@/components/blocks/GlobalStylesContent';
import { CustomerService } from '@/components/content/CustomerService';
import { Notifications } from '@/components/content/Notifications';
import { SessionErrorDialog } from '@/components/content/SessionErrorDialog';
import { PLPProviderContext } from '@/components/layouts/PLPProvider';
import { layoutManifest } from '@/components/layouts/manifest';
import { useStaticSettings } from '@/data/Settings';
import { PageBlockMeta, PageBlockProvider } from '@/data/context/pageBlock';
import { SettingProvider } from '@/data/context/setting';
import { Layout } from '@/data/types/Layout';
import { ThemeSettingsProvider } from '@/styles/theme';
import { CssBaseline, SxProps } from '@mui/material';
import { Theme, ThemeProvider } from '@mui/material/styles';
import { PreviousRouteProvider } from 'integration/hooks/usePreviousRoute';
import Head from 'next/head';
import { FC, useMemo } from 'react';

const DEFAULT_OG_IMAGE =
	'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/touchIcons/touch-icon-152px.png';

type PageProps = {
	meta: PageBlockMeta;
	layout: Layout;
	theme: Theme;
	additives?: { [key: string]: SxProps | ((theme: Theme) => SxProps) };
};

export const PageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const Layout = layout && layoutManifest[layout.name] ? layoutManifest[layout.name] : () => null;
	const themeAdditives = useMemo(() => ({ additives }), [additives]);

	return (
		<>
			<Head>
				<title>{meta?.title}</title>
				<meta name="description" content={meta?.description} />
				<meta name="keywords" content={meta?.keywords} />
				<meta name="pageIdentifier" content={meta?.pageIdentifier} />
				<meta name="pageId" content={meta?.pageId} />
				<meta name="pageGroup" content={meta?.pageGroup} />
				<link rel="icon" href="/favicon.jpeg" />
				{meta.canonical ? <link rel="canonical" href={meta.canonical} /> : null}
				<meta key="og:title" property="og:title" content={meta?.title} />
				<meta key="og:description" property="og:description" content={meta?.description} />
				<meta key="og:image" property="og:image" content={DEFAULT_OG_IMAGE} />
				<meta key="og:type" property="og:type" content="website" />
				<meta key="og:url" property="og:url" content={meta?.canonical} />
			</Head>
			<CustomerService />
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<ThemeSettingsProvider value={themeAdditives}>
					<PLPProviderContext>
						<GlobalStylesContent />
						<DpCookieDialogRecommendation />
						<PageBlockProvider value={{ meta }}>
							<AutoApplyBankPromotion>
								<PreviousRouteProvider>
									<Layout layout={layout} />
								</PreviousRouteProvider>
							</AutoApplyBankPromotion>
						</PageBlockProvider>
					</PLPProviderContext>
					<Notifications />
					<SessionErrorDialog />
				</ThemeSettingsProvider>
			</ThemeProvider>
		</>
	);
};

export const StaticPageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const { settings } = useStaticSettings();

	return settings ? (
		<SettingProvider value={settings}>
			<PageBlock meta={meta} layout={layout} theme={theme} additives={additives} />
		</SettingProvider>
	) : null;
};
